/**
 * @file src/ui/react/studio/entityMeta.ts
 * @description One canonical descriptor per collection entity kind for the arrange UI — the display label,
 * the count-row noun, and the design-lib icon name. Both the card (`StationTrackCard`: badge label + glyph)
 * and the drawer (`CardDrawer`: count row) read from HERE, so a new entity type is added once and the
 * label / icon / count noun can never silently drift apart across parallel switches. It also resolves an
 * entry to its page on the main site (`entityPageUrl`), for the same reason: the route segment and the id
 * field that names the entity belong on the same record as everything else about that entity kind.
 */
import { Constants } from '../../../config/Constants.js';

export interface EntityMeta {
  /** The canonical kind key — the drawer's filter key AND the `studio.filters.*` i18n key, so a
   *  translated entity noun is looked up the same way everywhere. Null = an unknown type. */
  kind: 'track' | 'orbiter' | 'world' | null;
  /** Title-cased badge label (no all-caps), in English — the fallback for `labelKey`. */
  label: string;
  /** Where the translated badge label lives. A key rather than a lookup done here, because this
   *  module is a table of facts about entity kinds and is read in contexts with no i18n booted;
   *  whoever RENDERS the label translates it. Not `studio.filters.*`, which is the same noun for a
   *  different job — those interpolate into a sentence and are lower-cased in Spanish for it. */
  labelKey: string;
  /** Lowercase singular noun for the count row ("3 audios"), in English — the fallback for
   *  `countKey`. */
  noun: string;
  /** Where the translated count row lives, as an i18next plural key (`_one` / `_other`). A whole
   *  phrase per kind rather than a noun this app pluralises itself: "audio" + "s" happens to be
   *  right in Spanish and "mundo" + "s" happens to be right too, but that is luck, not a rule —
   *  and no rule an app can write covers every language it might be read in. Written out rather
   *  than built from the kind, so the keys stay greppable for translators (the same reason
   *  `OPEN_PAGE_KEY` in the drawer is a table). */
  countKey: string;
  /** The design-lib `Icon name` for the entity glyph. */
  icon: string;
  /** The design-lib entity-frame class for the cover image (entity-frames.css, shipped in the
   *  lib's precompiled styles.css) — each core entity reads by the SHAPE of its frame across the
   *  whole platform: track = square, orbiter = hexagon, world = circle. The same classes the
   *  site's mini-cards use. Null = no signature shape (the default rounded tile). */
  frameClass: string | null;
  /** The design-lib entity accent token (`--ps-entity-*`, tokens.css) — one accent hue per entity
   *  type, the same hue the site uses (e.g. the four-corner card borders). Tints the type badge. */
  colorVar: string;
  /** The main site's entity-page segment (`<root>/<sitePath>/<id>`) — the dedicated page this card's
   *  entity has on the website, which the card context menu opens. Null = no page of its own (a
   *  generic item), so the menu offers nothing to open. */
  sitePath: string | null;
  /** Which id field on a collection entry names THIS entity (see `collectionDataService`). Paired with
   *  `sitePath` so the id and the page segment can never drift apart. */
  idKey: 'trackId' | 'orbiterId' | 'entangledWorldId' | null;
}

const TRACK: EntityMeta = { kind: 'track', label: 'Audio', labelKey: 'entity.label.track', noun: 'audio', countKey: 'entity.count.track', icon: 'track', frameClass: 'track-square-frame', colorVar: '--ps-entity-track', sitePath: 'audio', idKey: 'trackId' };
const ORBITER: EntityMeta = { kind: 'orbiter', label: 'Orbiter', labelKey: 'entity.label.orbiter', noun: 'orbiter', countKey: 'entity.count.orbiter', icon: 'orbiter', frameClass: 'hexagon-mask', colorVar: '--ps-entity-orbiter', sitePath: 'orbiter', idKey: 'orbiterId' };
const WORLD: EntityMeta = { kind: 'world', label: 'World', labelKey: 'entity.label.world', noun: 'world', countKey: 'entity.count.world', icon: 'entangled-world', frameClass: 'user-square-frame', colorVar: '--ps-entity-world', sitePath: 'entangled-world', idKey: 'entangledWorldId' };
const ITEM: EntityMeta = { kind: null, label: 'Item', labelKey: 'entity.label.item', noun: 'card', countKey: 'entity.count.item', icon: 'collection', frameClass: null, colorVar: '--ps-entity-collection', sitePath: null, idKey: null };
// A collection is not one of the three things a stage can hold, so its `kind` stays null — the
// drawer's filters are about what can be dealt onto a stage and a collection is the thing being
// dealt FROM. It is here because the downloaded-library page shows collections as cards, and the
// label / icon / accent for an entity kind belong on this record rather than in that page.
const COLLECTION: EntityMeta = { kind: null, label: 'Collection', labelKey: 'entity.label.collection', noun: 'collection', countKey: 'entity.count.collection', icon: 'collection', frameClass: 'collection-square-frame', colorVar: '--ps-entity-collection', sitePath: null, idKey: null };

/** Resolve a raw `entityType` string to its canonical descriptor (unknown → a generic "Item" / "card"). */
export function entityMeta(entityType: string | null | undefined): EntityMeta {
  switch (entityType) {
    case 'track':
      return TRACK;
    case 'orbiter':
      return ORBITER;
    case 'world':
    case 'entangled-world':
      return WORLD;
    case 'collection':
      return COLLECTION;
    default:
      return ITEM;
  }
}

/** The identity fields a collection entry carries (`api/collectionDataService.js`): the display type plus
 *  exactly one populated id. Display-only card fields live on `StationCardEntry`. */
export interface EntityRef {
  entityType?: string | null;
  trackId?: string | null;
  orbiterId?: string | null;
  entangledWorldId?: string | null;
}

/**
 * The entry's own page on the main site, or null when it has none (an unknown type, or a typed entry whose
 * id never arrived). Reads the id belonging to the entry's TYPE, so a track card carrying its related
 * orbiter/world ids can never open a sibling's page. `Constants.ROOT_BASE` is already trailing-slash
 * stripped and already falls back to this document's origin; the site adds the locale prefix itself.
 */
export function entityPageUrl(entry: EntityRef | null | undefined): string | null {
  const meta = entityMeta(entry?.entityType);
  if (!entry || !meta.sitePath || !meta.idKey) return null;
  const id = entry[meta.idKey];
  if (typeof id !== 'string' || !id.trim()) return null;
  return `${Constants.ROOT_BASE}/${meta.sitePath}/${encodeURIComponent(id)}`;
}
